import { CommonModule } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'

import { BaseComponent } from '../../shared'
import { PurchasingMatchResult } from '../../shared/data-models'
import { FeatureType } from '../../shared/enums'

@Component({
    selector: 'app-purchasing-reconciliation',
    templateUrl: './purchasing-reconciliation.component.html',
    styleUrls: ['./purchasing-reconciliation.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink],
})
export class PurchasingReconciliationComponent extends BaseComponent implements OnInit {
    public results: PurchasingMatchResult[] = []
    public filteredResults: PurchasingMatchResult[] = []
    public selectedResult: PurchasingMatchResult | null = null

    public filterNeedsReview = false
    public filterOnHold = false

    public emailing = false
    public emailModel = { toEmail: '', ccEmail: '', subject: '', message: '', notes: '' }
    public resolutionNotes = ''

    public isProcessing = false

    public get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.PurchasingReconciliationEdit])
    }

    constructor() {
        super()
        this.isInitialLoading = false
    }

    ngOnInit(): void {
        this.loadResults()
    }

    private loadResults(): void {
        this.services.data.http.get<PurchasingMatchResult[]>('/api/PurchasingReconciliation/GetList').subscribe({
            next: result => {
                this.results = result || []
                this.applyFilter()
            },
            error: () => this.services.notification.error('Failed to load reconciliation queue'),
        })
    }

    public applyFilter(): void {
        this.filteredResults = this.results.filter(r => {
            if (this.filterOnHold) return r.invoiceStatus === 'OnHold'
            if (this.filterNeedsReview)
                return !r.resolution && r.matchStatus !== 'Clean' && r.matchStatus !== 'Unmatched'
            return true
        })
    }

    public toggleNeedsReview(): void {
        this.filterNeedsReview = !this.filterNeedsReview
        if (this.filterNeedsReview) this.filterOnHold = false
        this.applyFilter()
    }

    public toggleOnHold(): void {
        this.filterOnHold = !this.filterOnHold
        if (this.filterOnHold) this.filterNeedsReview = false
        this.applyFilter()
    }

    public selectResult(result: PurchasingMatchResult): void {
        this.services.data.http
            .get<PurchasingMatchResult>(`/api/PurchasingReconciliation/GetDetail?invoiceId=${result.invoiceId}`)
            .subscribe({
                next: detail => {
                    this.selectedResult = detail
                    this.resolutionNotes = detail.resolutionNotes || ''
                },
                error: () => this.services.notification.error('Failed to load match detail'),
            })
    }

    public reEvaluate(): void {
        if (!this.selectedResult) return
        this.isProcessing = true
        this.services.data.http
            .post<PurchasingMatchResult>(
                `/api/PurchasingReconciliation/ReEvaluate?invoiceId=${this.selectedResult.invoiceId}`,
                {}
            )
            .subscribe({
                next: result => {
                    this.isProcessing = false
                    this.selectedResult = result
                    this.services.notification.success('Match re-evaluated')
                    this.loadResults()
                },
                error: () => {
                    this.isProcessing = false
                    this.services.notification.error('Failed to re-evaluate')
                },
            })
    }

    public approve(): void {
        this.applyResolution('Approve')
    }

    public acceptVariance(): void {
        this.applyResolution('AcceptVariance')
    }

    public hold(): void {
        this.applyResolution('Hold')
    }

    private applyResolution(action: 'Approve' | 'AcceptVariance' | 'Hold'): void {
        if (!this.selectedResult) return
        this.isProcessing = true
        const payload = { invoiceId: this.selectedResult.invoiceId, notes: this.resolutionNotes }
        this.services.data.http.post<PurchasingMatchResult>(`/api/PurchasingReconciliation/${action}`, payload).subscribe({
            next: result => {
                this.isProcessing = false
                this.selectedResult = result
                this.services.notification.success('Resolution saved')
                this.loadResults()
            },
            error: () => {
                this.isProcessing = false
                this.services.notification.error('Failed to save resolution')
            },
        })
    }

    public openContactVendor(): void {
        if (!this.selectedResult) return
        this.emailModel = {
            toEmail: '',
            ccEmail: '',
            subject: `Invoice variance - PO ${this.selectedResult.poNumber || ''} / Invoice ${this.selectedResult.invoiceNumber || ''}`.trim(),
            message: '',
            notes: this.resolutionNotes,
        }
        this.emailing = true
    }

    public sendContactVendor(): void {
        if (!this.selectedResult) return
        const payload = {
            invoiceId: this.selectedResult.invoiceId,
            toEmail: this.emailModel.toEmail,
            ccEmail: this.emailModel.ccEmail,
            subject: this.emailModel.subject,
            message: this.emailModel.message,
            notes: this.emailModel.notes,
        }
        this.services.data.http.post('/api/PurchasingReconciliation/ContactVendor', payload).subscribe({
            next: () => {
                this.services.notification.success('Variance email sent to vendor')
                this.emailing = false
                this.loadResults()
                this.selectResult(this.selectedResult!)
            },
            error: err => this.services.notification.error(err.error?.error || 'Failed to send email'),
        })
    }

    public matchBadgeClass(status: string): string {
        switch ((status || '').toLowerCase()) {
            case 'clean':
                return 'bg-success'
            case 'quantityvariance':
                return 'bg-warning text-dark'
            case 'pricevariance':
                return 'bg-warning text-dark'
            case 'missingitems':
            case 'extraitems':
            case 'unmatched':
                return 'bg-danger'
            default:
                return 'bg-secondary'
        }
    }

    public resolutionBadgeClass(resolution: string): string {
        switch ((resolution || '').toLowerCase()) {
            case 'approved':
                return 'bg-success'
            case 'acceptedvariance':
                return 'bg-info text-dark'
            case 'onhold':
                return 'bg-danger'
            case 'vendorcontacted':
                return 'bg-primary'
            default:
                return 'bg-secondary'
        }
    }

    public lineClass(lineStatus: string): string {
        switch ((lineStatus || '').toLowerCase()) {
            case 'clean':
                return 'table-success'
            case 'quantityvariance':
            case 'pricevariance':
                return 'table-warning'
            case 'missing':
            case 'extra':
                return 'table-danger'
            default:
                return ''
        }
    }

    public hasVariances(result: PurchasingMatchResult): boolean {
        return result.matchStatus !== 'Clean' && result.matchStatus !== 'Unmatched'
    }
}
